import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import { INFO, FLOW } from './llm-data';
import postContent from './posts/interactive-llm-content';

const ViewAllCtx = React.createContext(false);

function InfoToggle({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="info-toggle">
      <button className="info-toggle-btn" onClick={() => setOpen(o => !o)}>
        <span className="info-toggle-icon">{open ? '−' : '+'}</span>
        {q}
      </button>
      {open && <p className="info-toggle-body">{a}</p>}
    </div>
  );
}

function InlineExpl({ id }: { id: string }) {
  const viewAll = useContext(ViewAllCtx);
  if (!viewAll) return null;
  const info = INFO[id];
  if (!info) return null;
  return (
    <div className="node-inline-expl">
      <p className="node-inline-expl-body">{info.body}</p>
      {info.toggles?.map((t, i) => <InfoToggle key={i} q={t.q} a={t.a} />)}
    </div>
  );
}

function Node({ id, label, flowLines, selected, onSelect }: {
  id: string;
  label: string;
  flowLines?: string[];
  selected: string | null;
  onSelect: (id: string | null, el: HTMLElement) => void;
}) {
  const viewAll = useContext(ViewAllCtx);
  const isSelected = !viewAll && selected === id;
  return (
    <>
      <button
        data-node-id={id}
        className={`diagram-node${isSelected ? ' diagram-node-selected' : ''}`}
        onClick={e => { if (!viewAll) onSelect(isSelected ? null : id, e.currentTarget); }}
      >
        <span className="node-label-text">{label}</span>
        {flowLines && (
          <div className="node-data-preview">
            {flowLines.map((line, i) => <div key={i} className="node-data-line">{line}</div>)}
          </div>
        )}
      </button>
      <InlineExpl id={id} />
    </>
  );
}

function InputNode({ selected, onSelect }: {
  selected: string | null;
  onSelect: (id: string | null, el: HTMLElement) => void;
}) {
  const viewAll = useContext(ViewAllCtx);
  const isSelected = !viewAll && selected === 'input';
  return (
    <>
      <div
        data-node-id="input"
        className={`diagram-node input-node${isSelected ? ' diagram-node-selected' : ''}`}
        onClick={e => { if (!viewAll) onSelect(isSelected ? null : 'input', e.currentTarget); }}
      >
        <span className="input-node-label">Input Text</span>
        <input
          className="input-node-field"
          placeholder='"The capital of France is"'
          onClick={e => e.stopPropagation()}
        />
      </div>
      <InlineExpl id="input" />
    </>
  );
}

function OutputNode({ selected, onSelect }: {
  selected: string | null;
  onSelect: (id: string | null, el: HTMLElement) => void;
}) {
  const viewAll = useContext(ViewAllCtx);
  const isSelected = !viewAll && selected === 'output';
  return (
    <>
      <div
        data-node-id="output"
        className={`diagram-node input-node${isSelected ? ' diagram-node-selected' : ''}`}
        onClick={e => { if (!viewAll) onSelect(isSelected ? null : 'output', e.currentTarget); }}
      >
        <span className="input-node-label">Output Text</span>
        <span className="output-node-value">Paris</span>
      </div>
      <InlineExpl id="output" />
    </>
  );
}

function ExpandableNode({ id, label, flowLines, selected, onSelect, onExpand, stacked }: {
  id: string;
  label: string;
  flowLines?: string[];
  selected: string | null;
  onSelect: (id: string | null, el: HTMLElement) => void;
  onExpand: () => void;
  stacked?: boolean;
}) {
  const viewAll = useContext(ViewAllCtx);
  const isSelected = !viewAll && selected === id;
  return (
    <>
      <div
        data-node-id={id}
        className={`diagram-node diagram-node-expandable${isSelected ? ' diagram-node-selected' : ''}${stacked ? ' diagram-node-stacked' : ''}`}
      >
        <div className="expandable-top">
          <button
            className="expandable-body"
            onClick={e => { if (!viewAll) onSelect(isSelected ? null : id, e.currentTarget.closest('[data-node-id]') as HTMLElement); }}
          >
            {label}
          </button>
          {!viewAll && <button className="expand-btn" onClick={onExpand}>expand +</button>}
        </div>
        {flowLines && (
          <div className="node-data-preview">
            {flowLines.map((line, i) => <div key={i} className="node-data-line">{line}</div>)}
          </div>
        )}
      </div>
      <InlineExpl id={id} />
    </>
  );
}

function ExpandGroup({ label, onCollapse, children, stacked }: {
  label: string;
  onCollapse: () => void;
  children: React.ReactNode;
  stacked?: boolean;
}) {
  const viewAll = useContext(ViewAllCtx);
  return (
    <div className={`expand-group${stacked ? ' expand-group-stacked' : ''}`}>
      <div className="expand-group-header">
        <span className="expand-group-label">{label}</span>
        {!viewAll && <button className="expand-group-collapse" onClick={onCollapse}>collapse −</button>}
      </div>
      {children}
    </div>
  );
}

const Arrow = () => <div className="diagram-arrow">↓</div>;

function StreamOp({ children }: { children: React.ReactNode }) {
  return <div className="stream-op">{children}</div>;
}

function ResidualFlow({ children, selected, onSelect }: {
  children: React.ReactNode;
  selected: string | null;
  onSelect: (id: string | null, el: HTMLElement) => void;
}) {
  const viewAll = useContext(ViewAllCtx);
  const isSelected = !viewAll && selected === 'residual';
  return (
    <>
      <div className="residual-flow">
        <div className="residual-blocks-col">{children}</div>
        <div className="residual-rail-col">
          <div className="residual-rail-line" />
          <div className="stream-add-node">⊕</div>
          <div className="residual-rail-line" />
          <button
            data-node-id="residual"
            className={`residual-label-btn${isSelected ? ' residual-label-btn-selected' : ''}`}
            onClick={e => { if (!viewAll) onSelect(isSelected ? null : 'residual', e.currentTarget); }}
          >
            residual stream
          </button>
          <div className="residual-rail-line" />
          <div className="stream-add-node">⊕</div>
          <div className="residual-rail-line" />
        </div>
      </div>
      <InlineExpl id="residual" />
    </>
  );
}

// ─── Attention detail ────────────────────────────────────────────────────────

const ATTN_TOKENS = ['The', 'capital', 'of', 'France', 'is'];
const ATTN_WEIGHTS = [
  [0.84, 0.05, 0.04, 0.05, 0.02],
  [0.05, 0.73, 0.11, 0.08, 0.03],
  [0.04, 0.11, 0.62, 0.18, 0.05],
  [0.03, 0.12, 0.09, 0.73, 0.03],
  [0.03, 0.07, 0.05, 0.81, 0.04],
];

function attnFill(w: number) {
  const r = Math.round(237 - w * 147);
  const g = Math.round(232 - w * 222);
  const b = Math.round(244 - w * 122);
  return `rgb(${r},${g},${b})`;
}

function QKVDiagram() {
  return (
    <svg viewBox="0 0 260 112" width="100%" style={{ display: 'block' }}>
      <defs>
        <marker id="qkv-arrow" markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto">
          <polygon points="0 0, 6 2.5, 0 5" fill="#9b6bb5" />
        </marker>
      </defs>
      {/* Input */}
      <rect x={90} y={4} width={80} height={26} rx={4} fill="#d8d8d8" stroke="#800080" strokeWidth={1.5} />
      <text x={130} y={21} textAnchor="middle" fontSize={10} fontWeight="600" fill="#333">residual (x)</text>
      {/* Arrows */}
      <line x1={100} y1={30} x2={40} y2={64} stroke="#800080" strokeWidth={1.5} markerEnd="url(#qkv-arrow)" />
      <line x1={130} y1={30} x2={130} y2={64} stroke="#9b6bb5" strokeWidth={1.5} markerEnd="url(#qkv-arrow)" />
      <line x1={160} y1={30} x2={220} y2={64} stroke="#b580b5" strokeWidth={1.5} markerEnd="url(#qkv-arrow)" />
      {/* Weight labels */}
      <text x={58} y={50} fontSize={9} fill="#800080" fontStyle="italic">W_q</text>
      <text x={134} y={50} fontSize={9} fill="#9b6bb5" fontStyle="italic">W_k</text>
      <text x={185} y={50} fontSize={9} fill="#b580b5" fontStyle="italic">W_v</text>
      {/* Q */}
      <rect x={8} y={64} width={60} height={26} rx={4} fill="#e8d8e8" stroke="#800080" strokeWidth={1.5} />
      <text x={38} y={81} textAnchor="middle" fontSize={13} fontWeight="700" fill="#800080">Q</text>
      {/* K */}
      <rect x={100} y={64} width={60} height={26} rx={4} fill="#ede4f5" stroke="#9b6bb5" strokeWidth={1.5} />
      <text x={130} y={81} textAnchor="middle" fontSize={13} fontWeight="700" fill="#9b6bb5">K</text>
      {/* V */}
      <rect x={192} y={64} width={60} height={26} rx={4} fill="#f2edf8" stroke="#b580b5" strokeWidth={1.5} />
      <text x={222} y={81} textAnchor="middle" fontSize={13} fontWeight="700" fill="#b580b5">V</text>
      {/* Sub-labels */}
      <text x={38} y={104} textAnchor="middle" fontSize={8} fill="#999">query</text>
      <text x={130} y={104} textAnchor="middle" fontSize={8} fill="#999">key</text>
      <text x={222} y={104} textAnchor="middle" fontSize={8} fill="#999">value</text>
    </svg>
  );
}

function AttentionHeatmap() {
  const cell = 34;
  const labelW = 48;
  const labelH = 40;
  const n = ATTN_TOKENS.length;
  const W = labelW + n * cell + 4;
  const H = labelH + n * cell + 16;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
      {/* Column headers */}
      {ATTN_TOKENS.map((t, j) => (
        <text key={`c${j}`}
          x={labelW + j * cell + cell / 2} y={labelH - 8}
          textAnchor="middle" fontSize={8.5} fill="#555">{t}</text>
      ))}
      {/* Row labels */}
      {ATTN_TOKENS.map((t, i) => (
        <text key={`r${i}`}
          x={labelW - 5} y={labelH + i * cell + cell / 2 + 3}
          textAnchor="end" fontSize={8.5} fill="#555">{t}</text>
      ))}
      {/* Cells */}
      {ATTN_WEIGHTS.map((row, i) =>
        row.map((w, j) => (
          <g key={`${i}-${j}`}>
            <rect
              x={labelW + j * cell} y={labelH + i * cell}
              width={cell - 1} height={cell - 1} rx={2}
              fill={attnFill(w)}
            />
            <text
              x={labelW + j * cell + cell / 2} y={labelH + i * cell + cell / 2 + 3}
              textAnchor="middle" fontSize={8}
              fill={w > 0.38 ? '#fff' : '#444'}
            >{Math.round(w * 100)}%</text>
          </g>
        ))
      )}
      {/* Axis labels */}
      <text x={labelW + (n * cell) / 2} y={H - 1} textAnchor="middle" fontSize={8} fill="#aaa">← keys (what each token offers) →</text>
    </svg>
  );
}

function MultiHeadViz() {
  const colors = ['#800080', '#9b6bb5', '#b090c8', '#c4aad6'];
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end' }}>
      {colors.map((c, i) => (
        <div key={i} style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ height: 30, background: c, borderRadius: 3, marginBottom: 4 }} />
          <span style={{ fontSize: '0.65rem', color: '#777' }}>h{i + 1}</span>
        </div>
      ))}
      <div style={{ paddingBottom: 18, paddingLeft: 4, fontSize: '0.72rem', color: '#aaa', whiteSpace: 'nowrap' }}>
        + 8 more
      </div>
    </div>
  );
}

function AttentionExpandedView() {
  return (
    <div className="node-detail-panel">
      <div className="detail-step">
        <div className="detail-step-label">1 — Project input to Q, K, V</div>
        <QKVDiagram />
      </div>
      <div className="detail-step">
        <div className="detail-step-label">2 — Score, attend, weight-sum</div>
        <div className="detail-formula">
          softmax( QK<sup>T</sup> / √d<sub>k</sub> ) · V
        </div>
        <div style={{ height: 6 }} />
        <AttentionHeatmap />
        <div className="detail-caption">rows = query tokens &nbsp;·&nbsp; each row sums to 100%</div>
      </div>
      <div className="detail-step">
        <div className="detail-step-label">3 — Run 12 heads in parallel, concat + project</div>
        <MultiHeadViz />
        <div className="detail-caption">each head learns different relationships (syntax, coreference, …)</div>
      </div>
    </div>
  );
}

// ─── FFN detail ──────────────────────────────────────────────────────────────

function FFNNetwork() {
  const nIn = 5, nHid = 13, nOut = 5;
  const W = 260, H = 200;
  const xIn = 36, xHid = 130, xOut = 224;

  const col = (n: number, x: number) =>
    Array.from({ length: n }, (_, i) => ({
      x,
      y: 14 + (i * (H - 46)) / Math.max(n - 1, 1),
    }));

  const ins = col(nIn, xIn);
  const hids = col(nHid, xHid);
  const outs = col(nOut, xOut);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
      {/* in → hid */}
      {ins.flatMap((a, i) =>
        hids.map((b, j) => (
          <line key={`ih${i}${j}`}
            x1={a.x + 7} y1={a.y} x2={b.x - 6} y2={b.y}
            stroke="#c4aad6" strokeWidth="0.7" opacity="0.32" />
        ))
      )}
      {/* hid → out */}
      {hids.flatMap((a, i) =>
        outs.map((b, j) => (
          <line key={`ho${i}${j}`}
            x1={a.x + 6} y1={a.y} x2={b.x - 7} y2={b.y}
            stroke="#c4aad6" strokeWidth="0.7" opacity="0.32" />
        ))
      )}
      {/* Input nodes */}
      {ins.map((p, i) => (
        <circle key={`in${i}`} cx={p.x} cy={p.y} r={7}
          fill="#d8d8d8" stroke="#800080" strokeWidth="1.5" />
      ))}
      {/* Hidden nodes */}
      {hids.map((p, i) => (
        <circle key={`h${i}`} cx={p.x} cy={p.y} r={5.5}
          fill="#e8d8e8" stroke="#9b6bb5" strokeWidth="1.2" />
      ))}
      {/* Output nodes */}
      {outs.map((p, i) => (
        <circle key={`out${i}`} cx={p.x} cy={p.y} r={7}
          fill="#d8d8d8" stroke="#800080" strokeWidth="1.5" />
      ))}
      {/* Dimension labels */}
      <text x={xIn} y={H - 14} textAnchor="middle" fontSize={9} fill="#800080" fontWeight="600">768</text>
      <text x={xHid} y={H - 14} textAnchor="middle" fontSize={9} fill="#9b6bb5" fontWeight="600">3072</text>
      <text x={xOut} y={H - 14} textAnchor="middle" fontSize={9} fill="#800080" fontWeight="600">768</text>
      <text x={xIn} y={H - 4} textAnchor="middle" fontSize={7.5} fill="#aaa">d_model</text>
      <text x={xHid} y={H - 4} textAnchor="middle" fontSize={7.5} fill="#aaa">4 × d_model</text>
      <text x={xOut} y={H - 4} textAnchor="middle" fontSize={7.5} fill="#aaa">d_model</text>
      {/* Operation labels */}
      <text x={(xIn + xHid) / 2} y={8} textAnchor="middle" fontSize={8} fill="#bbb">Linear W₁</text>
      <text x={(xHid + xOut) / 2} y={8} textAnchor="middle" fontSize={8} fill="#bbb">GELU → Linear W₂</text>
    </svg>
  );
}

function GELUCurve() {
  const W = 240, H = 96;
  const ml = 26, mr = 12, mt = 10, mb = 24;
  const xMin = -3, xMax = 3, yMin = -0.35, yMax = 3.3;

  const toSVG = (x: number, y: number): [number, number] => [
    ml + ((x - xMin) / (xMax - xMin)) * (W - ml - mr),
    H - mb - ((y - yMin) / (yMax - yMin)) * (H - mt - mb),
  ];

  function gelu(x: number) {
    return 0.5 * x * (1 + Math.tanh(Math.sqrt(2 / Math.PI) * (x + 0.044715 * x ** 3)));
  }

  const xs = Array.from({ length: 61 }, (_, i) => xMin + (i * (xMax - xMin)) / 60);

  const geluD = xs.map((x, i) => {
    const [sx, sy] = toSVG(x, gelu(x));
    return `${i === 0 ? 'M' : 'L'}${sx.toFixed(1)},${sy.toFixed(1)}`;
  }).join(' ');

  const reluD = xs.map((x, i) => {
    const [sx, sy] = toSVG(x, Math.max(0, x));
    return `${i === 0 ? 'M' : 'L'}${sx.toFixed(1)},${sy.toFixed(1)}`;
  }).join(' ');

  const [ax0, axY] = toSVG(xMin, 0);
  const [ax1] = toSVG(xMax, 0);
  const [axV, ayTop] = toSVG(0, yMax);

  // tick marks
  const ticks = [-2, -1, 1, 2].map(v => {
    const [tx, ty] = toSVG(v, 0);
    return { tx, ty, v };
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
      {/* Grid lines */}
      {ticks.map(({ tx, ty, v }) => (
        <g key={v}>
          <line x1={tx} y1={ty - 3} x2={tx} y2={ty + 3} stroke="#ccc" strokeWidth="1" />
          <text x={tx} y={ty + 12} textAnchor="middle" fontSize={7.5} fill="#bbb">{v}</text>
        </g>
      ))}
      {/* Axes */}
      <line x1={ax0} y1={axY} x2={ax1} y2={axY} stroke="#bbb" strokeWidth="1" />
      <line x1={axV} y1={axY + 4} x2={axV} y2={ayTop} stroke="#bbb" strokeWidth="1" />
      {/* ReLU */}
      <path d={reluD} fill="none" stroke="#ccc" strokeWidth="1.5" strokeDasharray="3,3" />
      {/* GELU */}
      <path d={geluD} fill="none" stroke="#800080" strokeWidth="2" strokeLinejoin="round" />
      {/* Legend */}
      <line x1={W - 70} y1={mt + 6} x2={W - 56} y2={mt + 6} stroke="#800080" strokeWidth="2" />
      <text x={W - 53} y={mt + 10} fontSize={8} fill="#800080">GELU</text>
      <line x1={W - 70} y1={mt + 18} x2={W - 56} y2={mt + 18} stroke="#ccc" strokeWidth="1.5" strokeDasharray="3,3" />
      <text x={W - 53} y={mt + 22} fontSize={8} fill="#bbb">ReLU</text>
      {/* Axis labels */}
      <text x={ax1 + 2} y={axY + 4} fontSize={8} fill="#aaa">x</text>
    </svg>
  );
}

function FFNExpandedView() {
  return (
    <div className="node-detail-panel">
      <div className="detail-step">
        <div className="detail-step-label">Two linear layers with GELU between — applied per token independently</div>
        <FFNNetwork />
      </div>
      <div className="detail-step">
        <div className="detail-step-label">GELU activation</div>
        <GELUCurve />
        <div className="detail-caption">smooth non-linearity; slightly negative for x &lt; 0, unlike ReLU (dashed) — lets gradients flow even for negative inputs</div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface ExplItem { id: string; top: number }
const OVERLAP_PX = 90;

export default function InteractiveLLM() {
  const [viewAll, setViewAll] = useState(false);
  const [llmExpanded, setLlmExpanded] = useState(false);
  const [embeddingsExpanded, setEmbeddingsExpanded] = useState(false);
  const [transformerExpanded, setTransformerExpanded] = useState(false);
  const [attentionExpanded, setAttentionExpanded] = useState(false);
  const [ffnExpanded, setFfnExpanded] = useState(false);
  const [outputExpanded, setOutputExpanded] = useState(false);
  const [explanations, setExplanations] = useState<ExplItem[]>([]);
  const diagramRef = useRef<HTMLDivElement>(null);

  const selected = explanations.length > 0 ? explanations[explanations.length - 1].id : null;
  const clearExpl = () => setExplanations([]);
  const explanationRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (viewAll) {
      setLlmExpanded(true);
      setEmbeddingsExpanded(true);
      setTransformerExpanded(true);
      setAttentionExpanded(true);
      setFfnExpanded(true);
      setOutputExpanded(true);
      setExplanations([]);
    }
  }, [viewAll]);

  useEffect(() => {
    if (explanations.length < 2) return;
    const active = explanations[explanations.length - 1];
    const activeEl = explanationRefs.current.get(active.id);
    if (!activeEl) return;
    const activeRect = activeEl.getBoundingClientRect();
    const toRemove = new Set<string>();
    for (const expl of explanations.slice(0, -1)) {
      const el = explanationRefs.current.get(expl.id);
      if (!el) continue;
      const rect = el.getBoundingClientRect();
      if (Math.min(rect.bottom, activeRect.bottom) > Math.max(rect.top, activeRect.top)) {
        toRemove.add(expl.id);
      }
    }
    if (toRemove.size > 0) {
      setExplanations(prev => prev.filter(e => !toRemove.has(e.id)));
    }
  }, [explanations]);

  const select = useCallback((id: string | null, el?: HTMLElement) => {
    if (!id) { setExplanations([]); return; }
    let top = 0;
    if (el && diagramRef.current) {
      const nodeRect = el.getBoundingClientRect();
      const containerRect = diagramRef.current.getBoundingClientRect();
      top = Math.max(0, nodeRect.top - containerRect.top);
    }
    setExplanations(prev => {
      if (prev.length > 0 && prev[prev.length - 1].id === id) return prev.slice(0, -1);
      const filtered = prev.filter(e => e.id !== id && Math.abs(e.top - top) > OVERLAP_PX);
      return [...filtered, { id, top }];
    });
  }, []);

  return (
    <ViewAllCtx.Provider value={viewAll}>
      <main className="content">
        <div className="page-heading-row">
          <h2 className="page-heading" style={{ margin: 0 }}>Interactive LLM</h2>
          <button className="view-all-btn" onClick={() => setViewAll(v => !v)}>
            {viewAll ? '← interactive' : 'view all →'}
          </button>
        </div>
        <p className="section-text">
          An interactive diagram of how a large language model (LLM) works. Click any block to learn more, or expand a block to see its internals.
        </p>
        <div style={{ marginBottom: '36px' }}>
          <InfoToggle
            q="What is this trying to answer?"
            a='Most explanations of LLMs either stay too vague ("it predicts the next word") or dive straight into matrix math. This diagram tries to sit in between — giving you a mental model of the actual components and what each one does, without requiring a background in machine learning. The goal is that after clicking through, you have a genuine sense of what ChatGPT is doing when it responds to you.'
          />
        </div>

        <div className={`llm-layout${viewAll ? ' llm-layout-viewall' : ''}`}>
          <div className="llm-diagram" ref={diagramRef}>

            <InputNode selected={selected} onSelect={select} />
            <Arrow />

            {!llmExpanded ? (
              <ExpandableNode
                id="llm" label="LLM"
                selected={selected} onSelect={select}
                onExpand={() => { setLlmExpanded(true); clearExpl(); }}
              />
            ) : (() => {
              const condensed = embeddingsExpanded || transformerExpanded || outputExpanded;
              return (
              <ExpandGroup
                label="LLM"
                onCollapse={() => {
                  setLlmExpanded(false); setEmbeddingsExpanded(false);
                  setTransformerExpanded(false); setAttentionExpanded(false);
                  setFfnExpanded(false); setOutputExpanded(false); clearExpl();
                }}
              >
                <Node
                  id="tokenizer" label="Tokenizer"
                  flowLines={condensed ? undefined : FLOW.afterTokenizer}
                  selected={selected} onSelect={select}
                />
                <Arrow />

                {!embeddingsExpanded ? (
                  <ExpandableNode
                    id="embeddings" label="Embeddings + Positional Encoding"
                    flowLines={condensed ? undefined : FLOW.afterPositionalEncoding}
                    selected={selected} onSelect={select}
                    onExpand={() => { setEmbeddingsExpanded(true); clearExpl(); }}
                  />
                ) : (
                  <ExpandGroup
                    label="Embeddings + Positional Encoding"
                    onCollapse={() => { setEmbeddingsExpanded(false); clearExpl(); }}
                  >
                    <Node
                      id="tokenembeddings" label="Token Embeddings"
                      flowLines={FLOW.afterTokenEmbeddings}
                      selected={selected} onSelect={select}
                    />
                    <Arrow />
                    <Node
                      id="positionalencoding" label="Positional Encoding"
                      flowLines={FLOW.afterPositionalEncoding}
                      selected={selected} onSelect={select}
                    />
                  </ExpandGroup>
                )}

                <Arrow />

                {!transformerExpanded ? (
                  <ExpandableNode
                    id="transformer" label="Transformer Block × N" stacked
                    flowLines={condensed ? undefined : FLOW.afterTransformer}
                    selected={selected} onSelect={select}
                    onExpand={() => { setTransformerExpanded(true); clearExpl(); }}
                  />
                ) : (
                  <ExpandGroup
                    label="Transformer Block × N" stacked
                    onCollapse={() => {
                      setTransformerExpanded(false);
                      setAttentionExpanded(false); setFfnExpanded(false);
                      clearExpl();
                    }}
                  >
                    <ResidualFlow selected={selected} onSelect={select}>
                      <StreamOp>
                        <Node id="layernorm" label="Layer Norm" selected={selected} onSelect={select} />
                        {!attentionExpanded ? (
                          <ExpandableNode
                            id="attention" label="Multi-Head Attention"
                            selected={selected} onSelect={select}
                            onExpand={() => { setAttentionExpanded(true); clearExpl(); }}
                          />
                        ) : (
                          <ExpandGroup
                            label="Multi-Head Attention"
                            onCollapse={() => { setAttentionExpanded(false); clearExpl(); }}
                          >
                            <AttentionExpandedView />
                          </ExpandGroup>
                        )}
                      </StreamOp>
                      <StreamOp>
                        <Node id="layernorm" label="Layer Norm" selected={selected} onSelect={select} />
                        {!ffnExpanded ? (
                          <ExpandableNode
                            id="ffn" label="Feed-Forward Network"
                            selected={selected} onSelect={select}
                            onExpand={() => { setFfnExpanded(true); clearExpl(); }}
                          />
                        ) : (
                          <ExpandGroup
                            label="Feed-Forward Network"
                            onCollapse={() => { setFfnExpanded(false); clearExpl(); }}
                          >
                            <FFNExpandedView />
                          </ExpandGroup>
                        )}
                      </StreamOp>
                    </ResidualFlow>
                  </ExpandGroup>
                )}

                <Arrow />
                <Node
                  id="unembedding" label="Unembedding"
                  flowLines={condensed ? undefined : FLOW.afterUnembedding}
                  selected={selected} onSelect={select}
                />
                <Arrow />

                {!outputExpanded ? (
                  <ExpandableNode
                    id="outputlayer" label="Output Layer"
                    flowLines={condensed ? undefined : FLOW.afterSoftmax}
                    selected={selected} onSelect={select}
                    onExpand={() => { setOutputExpanded(true); clearExpl(); }}
                  />
                ) : (
                  <ExpandGroup
                    label="Output Layer"
                    onCollapse={() => { setOutputExpanded(false); clearExpl(); }}
                  >
                    <Node
                      id="softmax" label="Softmax"
                      flowLines={FLOW.afterSoftmax}
                      selected={selected} onSelect={select}
                    />
                    <Arrow />
                    <Node
                      id="sampling" label="Token Sampling"
                      flowLines={FLOW.afterSampling}
                      selected={selected} onSelect={select}
                    />
                  </ExpandGroup>
                )}
              </ExpandGroup>
              );
            })()}

            <Arrow />
            <OutputNode selected={selected} onSelect={select} />

          </div>

          {!viewAll && (
            <div className="llm-explanation" style={{ minHeight: explanations.length > 0 ? Math.max(...explanations.map(e => e.top + 160)) : 120 }}>
              {explanations.length === 0 ? (
                <p className="explanation-placeholder">
                  Click a block to learn more, or use "expand" to see its internals.
                </p>
              ) : explanations.map((expl, i) => {
                const info = INFO[expl.id];
                if (!info) return null;
                const isActive = i === explanations.length - 1;
                return (
                  <div
                    key={expl.id}
                    ref={el => { if (el) explanationRefs.current.set(expl.id, el); else explanationRefs.current.delete(expl.id); }}
                    className={`explanation-inner${isActive ? '' : ' explanation-inner-dim'}`}
                    style={{ position: 'absolute', top: expl.top, width: '100%' }}
                  >
                    <span className="explanation-arrow">←</span>
                    <div>
                      <h3 className="explanation-title">{info.title}</h3>
                      <p className="explanation-body">{info.body}</p>
                      {info.toggles?.map((t, i) => <InfoToggle key={i} q={t.q} a={t.a} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {postContent && (
          <div className="blog-post" style={{ marginTop: '48px' }}>
            <ReactMarkdown>{postContent}</ReactMarkdown>
          </div>
        )}
      </main>
    </ViewAllCtx.Provider>
  );
}
